import { z } from "zod";
import { ImportJobState } from "./import.ts";
import { DisplayName, PasswordSignInRequest, UserId } from "./user.ts";

/**
 * Админская панель: то, что едет между `apps/admin` и `/admin/v1/*`.
 *
 * Отдельный файл, а не добавка к `user.ts`, по той же причине, по которой у
 * панели отдельный origin (раздел 1.1 docs/admin-panel-design.md): у поверхности
 * с наибольшими правами должно быть как можно меньше общего с публичным SPA. Из
 * общего barrel'а `index.ts` этот файл реэкспортируется, потому что типы
 * административных ответов секретом не являются: секрет это права, а не форма
 * JSON. Но зависимость направлена в одну сторону, и ни один экран ученика ничего
 * отсюда не читает.
 *
 * ⚠️ **Содержимого уроков здесь нет ни в одном типе, и появляться ему нельзя.**
 * Ни элементов доски, ни ответов на упражнения, ни чата, ни анкеты ученика: у
 * панели есть только счётчики и даты (разделы 5.2 и 9 того же документа).
 * Первая же схема, которая отдаст админу элементы доски, отменит это решение
 * целиком, и вернуть его будет нельзя: данные уже утекут в интерфейс.
 *
 * ⚠️ **Исключение одно и называется поимённо: заметки преподавателя о
 * неточностях (`feedback_note`), решение владельца 14.10.2026.** Их тип живёт
 * НЕ здесь, а в `./feedback-read.ts`, вкладка у них своя (`/feedback`), в
 * карточке человека и в остальных ответах панели заметок нет. Довод: их читает
 * тот же человек, что читал их скриптом на сервере, круг читателей не вырос,
 * поменялось место чтения. Поэтому исключение в силе, ПОКА `admin_at` стоит у
 * одного человека: день выдачи прав второму админу это день пересмотра
 * исключения отдельным решением владельца. Прецедентом «в панель можно
 * содержимое уроков» оно не является: второе исключение требует отдельного
 * решения владельца, записанного здесь же. Разбор в
 * docs/feedback-admin-design.md, раздел 2.
 *
 * Даты наружу миллисекундами эпохи, как во всём остальном API.
 */

/**
 * Область сессии. Защита от конкретной дыры, разбор в разделе 2.2 документа:
 * `installAuth` принимает токен и через `Authorization: Bearer`, поэтому без
 * области владелец сырого токена обычной сессии подставил бы его в `Bearer` на
 * `/admin/v1/*`, и проверка `admin_at` его бы пропустила, потому что он
 * действительно админ.
 *
 * ⚠️ Значение не принимается из тела запроса нигде и никогда. Админская сессия
 * рождается только в `POST /admin/v1/auth/login`.
 *
 * `view` это просмотр кабинета преподавателя админом, только на чтение (раздел
 * 12 документа): `user_id` у такой сессии это ПРЕПОДАВАТЕЛЬ, а смотрящий лежит в
 * `session.viewer_id`. Рождается она только обменом одноразового кода в
 * `POST /v1/auth/view`, живёт только в cookie (Bearer с ней это 401), и пишущие
 * ручки `/v1/*` отвечают ей 403 `view_only`.
 */
export const SessionScope = z.enum(["app", "admin", "view"]);
export type SessionScope = z.infer<typeof SessionScope>;

/**
 * Причина административного действия.
 *
 * Границы те же, что у `CHECK (length(reason) BETWEEN 3 AND 500)` на
 * `admin_action`: разъехавшись, они дали бы значение, которое проходит границу
 * приложения и не проходит базу.
 *
 * ⚠️ Обязательна, и это не церемония (раздел 4.3 документа): через полгода
 * только причина и делает журнал читаемым. «Заблокировал 15.08» не отвечает ни
 * на один вопрос, «спам-регистрация, писал ученикам» отвечает на все.
 *
 * ⚠️ Нижняя граница именно три символа, а не «непустая». Отказ на `«»` понятен, а
 * отказ на `«?»` выглядит придиркой, поэтому это стоит сказать прямо: одна буква
 * и `«--»` тоже получат `400 reason_required`. Причина такая же, как у самой
 * обязательности: причина из одного знака не отвечает ни на один вопрос, то есть
 * это способ обойти требование, не нарушая его формально.
 */
export const AdminReason = z.string().min(3).max(500);
export type AdminReason = z.infer<typeof AdminReason>;

/** Тело любого пишущего админского запроса. Ровно причина и ничего больше. */
export const AdminActionRequest = z.object({ reason: AdminReason });
export type AdminActionRequest = z.infer<typeof AdminActionRequest>;

/**
 * Вход админа. Форма та же, что у входа преподавателя, и это осознанно: это тот
 * же провайдер `password` и тот же scrypt, отличается только выдаваемая область
 * сессии и её срок жизни. Своя схема с теми же границами означала бы два места,
 * где задан предел длины пароля.
 */
export const AdminSignInRequest = PasswordSignInRequest;
export type AdminSignInRequest = z.infer<typeof AdminSignInRequest>;

/** Кто я. Панель грузится по этому ответу и по нему же решает, что сессия жива. */
export const AdminMeResponse = z.object({
  userId: UserId,
  displayName: DisplayName,
  /** Когда выданы права. Дата, а не признак: она отвечает ещё и на «когда». */
  adminAt: z.number().int(),
  scope: z.literal("admin"),
  sessionExpiresAt: z.number().int(),
  /**
   * Включена ли почта (`EMAIL_ENABLED`, docs/email-design.md, раздел 2). Панель
   * узнаёт это у того же процесса, который отправляет письма, поэтому с
   * сервером не разъедется. Выключена, и поля «отправить письмом» в панели нет.
   */
  emailEnabled: z.boolean(),
});
export type AdminMeResponse = z.infer<typeof AdminMeResponse>;

/** Вход в панель возвращает токен в теле по той же причине, что и обычный. */
export const AdminSessionResponse = AdminMeResponse.extend({
  token: z.string().min(1),
});
export type AdminSessionResponse = z.infer<typeof AdminSessionResponse>;

/** Провайдер и почта: строка «чем этот человек входит» в списке людей. */
export const AdminIdentityBrief = z.object({
  provider: z.string().min(1),
  /**
   * Почта, как её отдал провайдер.
   *
   * ⚠️ Ключом личности она не является и в поиске личности не участвует
   * (раздел 5.10 CLAUDE.md): ключ это пара `(провайдер, id у провайдера)`.
   * Здесь она только для показа человеку.
   */
  email: z.string().nullable(),
});
export type AdminIdentityBrief = z.infer<typeof AdminIdentityBrief>;

/**
 * Строка списка людей. Ровно то, что перечислено в разделе 5.1 документа: это
 * тот экран, который показал бы постороннюю регистрацию в момент, когда она
 * случилась.
 */
export const AdminUserSummary = z.object({
  userId: UserId,
  displayName: DisplayName,
  createdAt: z.number().int(),
  /** Вход привязан. `false` это гость, у которого ключ к истории только cookie. */
  claimed: z.boolean(),
  /** Обезличен, то есть удалён. Строка осталась, чтобы не рассыпались уроки. */
  anonymizedAt: z.number().int().nullable(),
  /**
   * Заблокирован. Отличается от обезличивания тем, что обратимо и ничего не
   * трогает, кроме доступа (раздел 3.1 документа).
   */
  blockedAt: z.number().int().nullable(),
  /** Права админа. Выдаются только руками в SQL, ручки для этого нет. */
  adminAt: z.number().int().nullable(),
  identities: z.array(AdminIdentityBrief),
  /** Максимум по всем личностям. `null` значит «ни разу не входил». */
  lastLoginAt: z.number().int().nullable(),
  /** Сколько у него учеников и сколько уроков, где он любая из сторон. */
  studentCount: z.number().int(),
  lessonCount: z.number().int(),
});
export type AdminUserSummary = z.infer<typeof AdminUserSummary>;

/**
 * Постраничность keyset по `app_user.id`, без `OFFSET`.
 *
 * `id` это `uuidv7`, ключ временно упорядочен, поэтому `ORDER BY id DESC` идёт
 * по первичному ключу и совпадает с хронологией. Отдельный индекс по
 * `created_at` не нужен и его сознательно нет (раздел 5.1 документа).
 */
export const AdminUserListResponse = z.object({
  users: z.array(AdminUserSummary),
  /** `id` последнего в выдаче. `null` означает «страниц больше нет». */
  nextCursor: UserId.nullable(),
});
export type AdminUserListResponse = z.infer<typeof AdminUserListResponse>;

export const AdminIdentityView = z.object({
  provider: z.string().min(1),
  providerUserId: z.string().min(1),
  email: z.string().nullable(),
  /**
   * `email_verified` провайдера. Показывается человеку, но ни одно решение о
   * доступе на нём не строится: пускаем по id у провайдера.
   */
  emailVerified: z.boolean().nullable(),
  linkedAt: z.number().int(),
  lastLoginAt: z.number().int().nullable(),
});
export type AdminIdentityView = z.infer<typeof AdminIdentityView>;

/**
 * Сессия в панели.
 *
 * ⚠️ `lastSeenAt` имеет точность около пятнадцати минут, и понижать её нельзя:
 * скользящее продление троттлится (`SESSION_SLIDE_THROTTLE_MS`), иначе каждый
 * запрос был бы записью в базу, а SPA на плохой сети ретраит. Экран обязан
 * подписывать это честно, а не притворяться реальным временем присутствия
 * (раздел 5.3 документа).
 *
 * ⚠️ `ip` показывается как есть, без «нормализации». Один взгляд на список тогда
 * выдаёт поломку целиком: если docker-сеть уедет за пределы доверенного
 * `172.16.0.0/12`, у всех сессий окажется один и тот же адрес прокси, и это
 * худший вариант из возможных (данные есть, и они врут).
 */
export const AdminSessionView = z.object({
  sessionId: z.string().uuid(),
  userId: UserId,
  displayName: DisplayName,
  scope: SessionScope,
  createdAt: z.number().int(),
  lastSeenAt: z.number().int(),
  expiresAt: z.number().int(),
  absoluteExpiresAt: z.number().int(),
  revokedAt: z.number().int().nullable(),
  revokedReason: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  /**
   * Кто смотрит, если это сессия области `view`, иначе `null`.
   *
   * ⚠️ Без этих полей просмотр на экране «кто активен» выглядит как активность
   * преподавателя с адресом админа (12.7 документа): `userId` и `displayName`
   * у такой строки это цель, а `ip` и `userAgent` это браузер смотрящего.
   *
   * Необязательны ради окна выкатки: вкладка новой панели на старом API их не
   * получит. Отсутствие читается как `null`.
   */
  viewerId: UserId.nullable().optional(),
  viewerName: DisplayName.nullable().optional(),
});
export type AdminSessionView = z.infer<typeof AdminSessionView>;

/**
 * Ответ `POST /admin/v1/users/:userId/view`: ссылка на просмотр кабинета.
 *
 * Вид `https://edu-app.../view#<код>`. Код во ФРАГМЕНТЕ, а не в пути: фрагмент
 * не уходит на сервер, не попадает ни в Referer, ни в access-лог Caddy. Живёт
 * 60 секунд и гасится первым использованием, поэтому панель открывает ссылку
 * сразу, а не показывает её человеку для копирования (12.4 документа).
 *
 * Отказ 404 `user_not_found` одинаков для всех причин: цели нет, это сам админ,
 * ученик, обезличенный или заблокированный.
 */
export const AdminViewLinkResponse = z.object({ url: z.string().url() });
export type AdminViewLinkResponse = z.infer<typeof AdminViewLinkResponse>;

/**
 * Живые сессии всех людей, постраничность keyset.
 *
 * ⚠️ Курсор здесь НЕ `id` последней строки, в отличие от трёх остальных списков, и
 * это следствие порядка: сессии сортируются по `last_seen_at DESC`, а не по ключу,
 * потому что вопрос экрана «кто активен». Значит в курсоре обязаны быть оба поля
 * сортировки, иначе строки с одинаковой активностью либо пропадут, либо
 * задвоятся. Формат строки это дело сервера, клиент возвращает её как есть.
 *
 * ⚠️ Раньше ответ был `{ sessions }` с потолком в 200 строк, без курсора и без
 * признака усечения. Это худший вид ответа, какой бывает у инструмента
 * наблюдения: сто одна живая сессия и тысяча живых сессий выглядели на экране
 * одинаково, и в обоих случаях экран говорил «столько и есть».
 */
export const AdminSessionListResponse = z.object({
  sessions: z.array(AdminSessionView),
  /** `null` означает «страниц больше нет», то есть выдача полная. */
  nextCursor: z.string().min(1).nullable(),
});
export type AdminSessionListResponse = z.infer<typeof AdminSessionListResponse>;

/**
 * Пара «преподаватель и ученик» глазами панели.
 *
 * `side` это позиция ЭТОГО человека в паре, а не его роль: роли в схеме нет и не
 * будет, один человек законно бывает обеими сторонами (раздел 5.10 CLAUDE.md).
 */
export const AdminPairView = z.object({
  side: z.enum(["teacher", "student"]),
  /** Кто на другой стороне. */
  otherUserId: UserId,
  otherDisplayName: DisplayName,
  createdAt: z.number().int(),
  unlinkedAt: z.number().int().nullable(),
  archiveVisible: z.boolean(),
});
export type AdminPairView = z.infer<typeof AdminPairView>;

/**
 * Уроки человека: только счёт и дата последнего.
 *
 * ⚠️ Названий уроков здесь нет, и это не экономия полей. Название урока пишет
 * преподаватель, и оно бывает содержательным («разбор ошибок Кати»), а панель не
 * читает содержимое уроков (раздел 5.2 документа).
 */
export const AdminLessonStats = z.object({
  total: z.number().int(),
  lastAt: z.number().int().nullable(),
});
export type AdminLessonStats = z.infer<typeof AdminLessonStats>;

/** Материалы: сколько файлов и сколько занято. Ни имён, ни содержимого. */
export const AdminMediaStats = z.object({
  files: z.number().int(),
  bytes: z.number().int(),
});
export type AdminMediaStats = z.infer<typeof AdminMediaStats>;

export const AdminConsentView = z.object({
  kind: z.enum(["service", "recording", "marketing"]),
  policyVersion: z.string(),
  grantedAt: z.number().int(),
  revokedAt: z.number().int().nullable(),
});
export type AdminConsentView = z.infer<typeof AdminConsentView>;

/**
 * Что бывает записано в журнале входов.
 *
 * ⚠️ Пароль, токен и `code` из OAuth сюда не попадают ни в каком виде, а
 * `reason` это КОД отказа, а не текст исключения: текст однажды принесёт с собой
 * чужие данные (раздел 4.2 документа).
 */
export const AdminAuthEventKind = z.enum([
  "login_ok",
  "login_fail",
  "signup",
  "logout",
  "blocked_attempt",
  "admin_login_ok",
  "admin_login_fail",
  /**
   * Смена своего пароля из кабинета. Успех и промах разными значениями: промах
   * это не неудачный вход, а человек, УЖЕ сидящий в открытой сессии и
   * подбирающий текущий пароль. В ленте это разные тревоги.
   */
  "password_change",
  "password_change_fail",
]);
export type AdminAuthEventKind = z.infer<typeof AdminAuthEventKind>;

export const AdminAuthEventView = z.object({
  id: z.string().uuid(),
  at: z.number().int(),
  kind: AdminAuthEventKind,
  provider: z.string().nullable(),
  userId: UserId.nullable(),
  displayName: DisplayName.nullable(),
  /** Что ввели в поле логина. Нужно для попыток по несуществующему логину. */
  login: z.string().nullable(),
  reason: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type AdminAuthEventView = z.infer<typeof AdminAuthEventView>;

export const AdminAuthEventListResponse = z.object({
  events: z.array(AdminAuthEventView),
  nextCursor: z.string().uuid().nullable(),
});
export type AdminAuthEventListResponse = z.infer<typeof AdminAuthEventListResponse>;

export const AdminActionKind = z.enum([
  "block",
  "unblock",
  "revoke_session",
  "revoke_all_sessions",
  /** Приглашение преподавателя (раздел 9 документа). `target_id` это id приглашения. */
  "invite_issue",
  "invite_revoke",
  /**
   * Заявка «позвать коллегу» (docs/colleague-referral-design.md, 2.3): закрыта
   * выпуском приглашения или без него. `target_id` это id заявки,
   * `target_user_id` позвавший.
   */
  "referral_invite",
  "referral_dismiss",
]);
export type AdminActionKind = z.infer<typeof AdminActionKind>;

/**
 * Строка журнала админских действий.
 *
 * ⚠️ Этот журнал не удаляется никогда, в отличие от журнала входов: он
 * крошечный (десятки строк в месяц) и он единственный ответ на вопрос «кто это
 * сделал» (раздел 4.3 документа).
 */
export const AdminActionView = z.object({
  id: z.string().uuid(),
  at: z.number().int(),
  adminUserId: UserId,
  adminDisplayName: DisplayName,
  action: AdminActionKind,
  targetUserId: UserId.nullable(),
  targetDisplayName: DisplayName.nullable(),
  /** Идентификатор объекта действия, когда это не человек: например сессия. */
  targetId: z.string().uuid().nullable(),
  reason: AdminReason,
  ip: z.string().nullable(),
});
export type AdminActionView = z.infer<typeof AdminActionView>;

export const AdminActionListResponse = z.object({
  actions: z.array(AdminActionView),
  nextCursor: z.string().uuid().nullable(),
});
export type AdminActionListResponse = z.infer<typeof AdminActionListResponse>;

/**
 * Карточка человека одним ответом.
 *
 * Одним, а не восемью: экран открывается переходом из списка, и каждый лишний
 * круг по сети человек видит как подвисание. Та же причина, что у карточки
 * ученика в кабинете.
 */
export const AdminUserDetail = z.object({
  user: AdminUserSummary,
  identities: z.array(AdminIdentityView),
  /** Живые и отозванные вместе: отзыв это состояние, которое надо видеть. */
  sessions: z.array(AdminSessionView),
  /**
   * Сессий у человека больше, чем влезло в `ADMIN_CARD_SESSION_LIMIT`.
   *
   * ⚠️ Признак обязателен именно потому, что постраничности здесь нет и не
   * планируется: без него ровно пятьдесят строк в карточке читаются как «всего
   * пятьдесят», а это бывает неправдой. Гоняться за полной историей входов в
   * карточке незачем, а вот молчать об усечении нельзя.
   */
  sessionsTruncated: z.boolean(),
  pairs: z.array(AdminPairView),
  lessons: AdminLessonStats,
  media: AdminMediaStats,
  consents: z.array(AdminConsentView),
  /** Последние события входа этого человека. Полная лента живёт своей ручкой. */
  events: z.array(AdminAuthEventView),
  /**
   * Причина блокировки. Показывается ТОЛЬКО в панели: пользователю её не
   * отдаёт ни одна ручка, она написана для админа (раздел 3.2 документа).
   */
  blockedReason: z.string().nullable(),
  blockedByUserId: UserId.nullable(),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetail>;

/** Что вернула блокировка. Число выкинутых из комнат нужно для подтверждения. */
export const AdminBlockResponse = z.object({
  blockedAt: z.number().int(),
  revokedSessions: z.number().int(),
  /**
   * Из скольких живых уроков человека попросили выйти.
   *
   * ⚠️ Отказ LiveKit блокировку не роняет: база это источник истины, комната это
   * следствие (раздел 3.4 документа). Поэтому число здесь бывает меньше, чем
   * живых уроков, и это не ошибка ответа.
   */
  removedFromRooms: z.number().int(),
});
export type AdminBlockResponse = z.infer<typeof AdminBlockResponse>;

export const AdminRevokeResponse = z.object({
  revoked: z.number().int(),
});
export type AdminRevokeResponse = z.infer<typeof AdminRevokeResponse>;

/* ══════════════════════════ инвентарь (раздел 5.5) ══════════════════════════ */

/**
 * Сколько файлов, сколько байт и сколько времени.
 *
 * ⚠️ `durationMs` это `null`, когда длительности нет НИ У ОДНОГО файла в корзине
 * разреза, и это не то же самое, что нуль. Нуль значил бы «файлы есть, и все они
 * нулевой длины», а `null` значит «величина к этим файлам не применима»: у
 * картинок и PDF длительности не бывает вовсе. Подставить сюда нуль означало бы
 * показать «0 с» под колонкой картинок.
 */
export const AdminFileTally = z.object({
  files: z.number().int(),
  bytes: z.number().int(),
  durationMs: z.number().int().nullable(),
});
export type AdminFileTally = z.infer<typeof AdminFileTally>;

/**
 * Корзина разреза по файлам: значение и два счёта, живое и удалённое.
 *
 * ⚠️ **Живое и удалённое считаются раздельно и подписываются раздельно**
 * (раздел 5.5 документа). У `media_file` есть корзина на 30 дней, поэтому
 * «сколько файлов у человека» без уточнения это число, которое не сходится ни с
 * квотой, ни с тем, что человек видит у себя. Сложить их в одну величину нельзя
 * ни на сервере, ни на экране.
 *
 * `key` это `null`, когда в базе стоял `NULL`. Показывается как «не определено», а
 * не прячется: пустое значение там, где его не ждали, это и есть находка.
 */
export const AdminFileBucket = z.object({
  key: z.string().nullable(),
  live: AdminFileTally,
  deleted: AdminFileTally,
});
export type AdminFileBucket = z.infer<typeof AdminFileBucket>;

/**
 * Разрез по файлам с потолком строк.
 *
 * ⚠️ `distinct` обязателен рядом с `rows`: разных `content_type` бывает больше,
 * чем строк на экране, и двадцать строк без общего числа читаются как «типов
 * ровно двадцать». То же правило, по которому у списка сессий есть `nextCursor`, а
 * у карточки `sessionsTruncated`: молчаливой обрезки в панели не бывает.
 */
export const AdminFileBreakdown = z.object({
  rows: z.array(AdminFileBucket),
  /** Сколько всего различных значений, включая не показанные. */
  distinct: z.number().int(),
  truncated: z.boolean(),
});
export type AdminFileBreakdown = z.infer<typeof AdminFileBreakdown>;

/** Значение и число наблюдений. Ключ `null` это `NULL` в базе. */
export const AdminCountRow = z.object({
  key: z.string().nullable(),
  count: z.number().int(),
});
export type AdminCountRow = z.infer<typeof AdminCountRow>;

/**
 * Разрез со знаменателем.
 *
 * ⚠️ `total` едет вместе со строками намеренно, и доля считается на экране из этих
 * двух чисел, а не приезжает готовым процентом. Причина в правиле 2 подраздела
 * 5.6: доля без числа наблюдений производит уверенность, которой нет («100%
 * релея» на двух сессиях). Отдав только процент, сервер сделал бы честный показ
 * невозможным.
 */
export const AdminBreakdown = z.object({
  total: z.number().int(),
  rows: z.array(AdminCountRow),
  distinct: z.number().int(),
  truncated: z.boolean(),
});
export type AdminBreakdown = z.infer<typeof AdminBreakdown>;

/**
 * Что человек создал, одной строкой таблицы «по людям».
 *
 * ⚠️ Ни одного названия: ни имени файла, ни заголовка заготовки, ни темы урока.
 * Только количества, размеры и даты (раздел 5.5 документа). Имя файла это уже
 * содержимое, а панель содержимого не читает, и полей под него нет даже в схеме
 * ответа: граница, которую нельзя нарушить случайно, дешевле границы, которую
 * помнят.
 */
export const AdminInventoryOwner = z.object({
  userId: UserId,
  displayName: DisplayName,
  live: AdminFileTally,
  deleted: AdminFileTally,
  plans: z.number().int(),
  lessons: z.number().int(),
  /** Попыток упражнений. Счёт, а не ответы: ответы это содержимое урока. */
  attempts: z.number().int(),
});
export type AdminInventoryOwner = z.infer<typeof AdminInventoryOwner>;

/** Уроки по состояниям. Удалённые отдельной величиной, как и файлы в корзине. */
export const AdminLessonTally = z.object({
  created: z.number().int(),
  live: z.number().int(),
  ended: z.number().int(),
  deleted: z.number().int(),
});
export type AdminLessonTally = z.infer<typeof AdminLessonTally>;

/**
 * Инвентарь: что люди реально создают (раздел 5.5 документа).
 *
 * ⚠️ Инструментирования под этот ответ нет ни одной строки, всё уже лежит в базе.
 * Это десяток агрегирующих запросов: ноль нового хранения, ноль новых согласий,
 * ноль риска по 152-ФЗ.
 */
export const AdminInventoryResponse = z.object({
  /** Кто в разрезе. `null` значит «по всем людям». */
  scope: z.object({ userId: UserId, displayName: DisplayName }).nullable(),
  /** Итог по файлам. Складывается из `byKind`, отдельным запросом не считается. */
  files: z.object({ live: AdminFileTally, deleted: AdminFileTally }),
  /** `kind` мы выбрали сами, поэтому корзин мало и потолок им не нужен. */
  byKind: z.array(AdminFileBucket),
  /**
   * ⚠️ Разрез, который интереснее предыдущего: `kind` это наша классификация, а
   * `content_type` показывает, что люди приносят на самом деле (раздел 5.5).
   */
  byContentType: AdminFileBreakdown,
  plans: z.object({
    live: z.number().int(),
    deleted: z.number().int(),
    /** Слайдов в живых заготовках. Размер работы, а не число документов. */
    slides: z.number().int(),
  }),
  lessons: AdminLessonTally,
  attempts: z.number().int(),
  /** Задания конвертации по состояниям: видно, сколько импортов падает. */
  convertJobs: AdminBreakdown,
  schedule: z.object({ series: z.number().int(), slots: z.number().int() }),
  /** Пары. Отвязанные отдельно: связь не удаляется, у неё ставится дата. */
  pairs: z.object({ live: z.number().int(), unlinked: z.number().int() }),
  /**
   * Разрез по людям, по убыванию занятого места.
   *
   * ⚠️ `null`, а не пустой массив, когда запрошен разрез по человеку: пустой
   * массив читался бы как «никто ничего не создал», а тут таблица просто не
   * запрашивалась.
   */
  owners: z
    .object({
      rows: z.array(AdminInventoryOwner),
      /** Сколько людей вообще что-нибудь создали, включая не показанных. */
      total: z.number().int(),
      truncated: z.boolean(),
    })
    .nullable(),
});
export type AdminInventoryResponse = z.infer<typeof AdminInventoryResponse>;

/* ═════════════════════════════ сеть (раздел 5.6) ════════════════════════════ */

/**
 * Величина процентилями и число наблюдений рядом с ней.
 *
 * ⚠️ **Проценты, а не средние**, и это правило 1 подраздела 5.6: на сетевых
 * данных среднее прячет хвост, а хвост и есть продуктовая проблема. Среднего в
 * этой схеме нет ни одного поля, и добавлять его не надо: рядом со средним
 * процентили читают через него.
 *
 * ⚠️ **`n` обязателен**, это правило 2 того же подраздела. При восьмидесяти
 * сессиях любой разрез даёт корзины по две-три штуки, и «P99 = 5 секунд» на двух
 * замерах читается как вывод. При `n = 0` все три процентиля `null`, а не нули:
 * нуль это измерение, а `null` это его отсутствие.
 */
export const AdminPercentiles = z.object({
  n: z.number().int(),
  p50: z.number().nullable(),
  p90: z.number().nullable(),
  p99: z.number().nullable(),
});
export type AdminPercentiles = z.infer<typeof AdminPercentiles>;

/**
 * Оператор: то, ради чего экран требуется разделом 9 CLAUDE.md.
 *
 * ⚠️ Все три поля бывают `null` разом, и это законный исход, а не поломка. `asn`
 * и `asn_org` заполняются на создании сессии поиском усечённого префикса в
 * справочнике `ip_asn_range`, и справочник может быть не загружен вовсе (тогда
 * пусты все строки) либо не содержать этого префикса (тогда пуста одна). `region`
 * пуст ВСЕГДА и по решению владельца: область в смысле раздела 3.1 CLAUDE.md
 * (Брянская против Москвы) это city-level база с другой лицензией, а код страны
 * из `ip_asn_range` регионом не является.
 */
export const AdminNetworkOperator = z.object({
  asn: z.number().int().nullable(),
  asnOrg: z.string().nullable(),
  region: z.string().nullable(),
  sessions: z.number().int(),
  /** Знаменатель доли релея: сессий, где тип кандидата вообще записан. */
  candidateKnown: z.number().int(),
  relaySessions: z.number().int(),
  /** ⚠️ Только новый инструмент, как и `connectMs` в корне ответа. */
  connectMs: AdminPercentiles,
  /** Сессий, где ICE перезапускался хотя бы раз. */
  withRestart: z.number().int(),
  /** Сессий с записанной причиной отключения. */
  withDisconnect: z.number().int(),
});
export type AdminNetworkOperator = z.infer<typeof AdminNetworkOperator>;

/**
 * Строка распределения разрешения.
 *
 * ⚠️ Ради этого разреза экран и стоит делать первым (подраздел 5.6). В разделе 10
 * CLAUDE.md записана незакрытая задача «мыло и замирания на M2»: установлено, что
 * десктоп ПРИНИМАЕТ 180x320 при публикуемых 720, то есть подписка идёт на нижний
 * слой. Здесь это перестаёт быть разовым наблюдением и становится величиной с
 * долей и числом наблюдений.
 */
export const AdminResolutionRow = z.object({
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  samples: z.number().int(),
  /** В скольких сессиях такое разрешение встречалось. */
  sessions: z.number().int(),
});
export type AdminResolutionRow = z.infer<typeof AdminResolutionRow>;

/**
 * Замеры одной стороны соединения: `pub` это то, что мы отдаём, `sub` это то, что
 * принимаем.
 *
 * ⚠️ Половины НЕ симметричны, и складывать их нельзя. Сборщик на клиенте
 * (`apps/web/src/features/room/telemetry.ts`) кладёт джиттер, потери и замирания
 * только в `sub`, а причину ограничения качества только в `pub`. Поэтому пустая
 * величина здесь означает «эта сторона её не измеряет», а не «всё хорошо», и
 * `null` в ответе честнее нуля.
 */
export const AdminNetworkSide = z.object({
  samples: z.number().int(),
  /** Сколько разных сессий дали эти замеры: знаменатель для долей по сессиям. */
  sessions: z.number().int(),
  /* Мгновенные величины: процентили считаются по ЗАМЕРАМ, `n` это замеры. */
  rttMs: AdminPercentiles,
  jitterMs: AdminPercentiles,
  fps: AdminPercentiles,
  /**
   * ⚠️ **Три накопительных счётчика, и у них процентили по СЕССИЯМ, а не по
   * замерам.** `packets_lost`, `freeze_count` и `freeze_ms` приходят от браузера
   * накопительно с начала соединения (`cur.freezeCount`, `cur.packetsLost` в
   * `apps/web/src/features/room/stats.ts`), поэтому замер на пятой минуте
   * заведомо больше замера на первой. Процентиль по такому ряду это процентиль
   * по длине сессии, а не по качеству: у одного плохого урока на час было бы
   * двенадцать «плохих» наблюдений, у одного плохого урока на пять минут одно.
   * Поэтому сначала берётся максимум внутри сессии, и только потом процентиль по
   * сессиям, а суффикс `Max` в имени поля есть ровно для того, чтобы это нельзя
   * было прочитать иначе. `n` здесь это число сессий.
   */
  packetsLostMax: AdminPercentiles,
  freezeCountMax: AdminPercentiles,
  freezeMsMax: AdminPercentiles,
  resolutions: z.object({
    rows: z.array(AdminResolutionRow),
    /** Замеров с известным разрешением: знаменатель доли. */
    withSize: z.number().int(),
    /**
     * Замеров без разрешения.
     *
     * ⚠️ Обязательно наружу: у соединения без видео (уровень 3 лестницы
     * деградации, только аудио) размера нет вовсе, и без этого числа пустая
     * таблица читалась бы как «телеметрия не работает».
     */
    withoutSize: z.number().int(),
    distinct: z.number().int(),
    truncated: z.boolean(),
  }),
  /** Причины ограничения качества по замерам: `cpu`, `bandwidth`, `other`. */
  limitation: AdminBreakdown,
  /** Сессий, где причина была непустой хотя бы в одном замере. */
  limitationSessions: z.number().int(),
});
export type AdminNetworkSide = z.infer<typeof AdminNetworkSide>;

/** Отпечаток DTLS. Раздел 3.3 CLAUDE.md: его мониторят, а не решают один раз. */
export const AdminFingerprintRow = z.object({
  dtlsCipher: z.string().nullable(),
  tlsVersion: z.string().nullable(),
  count: z.number().int(),
});
export type AdminFingerprintRow = z.infer<typeof AdminFingerprintRow>;

/**
 * Сеть: единственные измерения, которых нет ни у кого (раздел 5.6 документа).
 *
 * Данные копятся с M5, а смотреть их было негде, и только поэтому телеметрия
 * была бесполезной. Свойство, которого нет ни у кликов, ни у инвентаря: условия
 * сети во время настоящего урока не воспроизводятся задним числом никак.
 */
export const AdminNetworkResponse = z.object({
  window: z.object({
    days: z.number().int(),
    /** Начало окна, миллисекундами эпохи. Считает сервер, показывает экран. */
    from: z.number().int(),
    sessions: z.number().int(),
  }),
  /** Тип выбранного кандидата. Проверка прогноза 25-35% релея из 5.4 CLAUDE.md. */
  candidateTypes: AdminBreakdown,
  /**
   * Протокол релея.
   *
   * ⚠️ Знаменатель здесь это ТОЛЬКО релеенные сессии, а не все: у прямого
   * соединения протокола релея не бывает, и посчитав долю от всех сессий, экран
   * показал бы «TLS у 4%» там, где верное число это «TLS у 78% релеев». Ключ
   * `null` внутри этого разреза значит «релей есть, протокол не записан».
   */
  relayProtocols: AdminBreakdown,
  /** Причина отключения. Ключ `null` это сессия, которая не отключалась. */
  disconnectReasons: AdminBreakdown,
  fingerprints: z.object({
    rows: z.array(AdminFingerprintRow),
    total: z.number().int(),
    distinct: z.number().int(),
    truncated: z.boolean(),
  }),
  /**
   * Время до соединения, ТОЛЬКО по новому инструменту (`connect_ms_source =
   * 'event'`).
   *
   * ⚠️ Две population в одной колонке, и смешивать их нельзя. До 15.08.2026
   * штамп ставился на тике опроса с интервалом 5000 мс, поэтому величина не
   * могла быть меньше пяти секунд: P50 в 5,5 с измерял интервал опроса, а не
   * сеть. После правки штамп ставится в момент перехода в `Connected`.
   * Процентиль по смеси это ложное число, поэтому старые сессии живут отдельно,
   * в `connectMsLegacy`, а не подмешиваются сюда и не выбрасываются молча.
   */
  connectMs: AdminPercentiles,
  /**
   * То же измерение старым инструментом: сессии без `connect_ms_source`.
   *
   * Показывается отдельной строкой с подписью про пятисекундный пол. Не
   * выбрасывается, потому что «величина исчезла» читалось бы как поломка
   * телеметрии, а не как смена инструмента, и потому что видеть артефакт
   * своими глазами полезнее, чем читать про него. Обнуляется само: срок
   * хранения телеметрии 180 суток, дальше старая population вымирает и `n`
   * здесь становится нулём навсегда.
   */
  connectMsLegacy: AdminPercentiles,
  restarts: z.object({
    /** Сессий с хотя бы одним перезапуском ICE. */
    withRestart: z.number().int(),
    /** Сумма перезапусков по всем сессиям окна. */
    total: z.number().int(),
  }),
  operators: z.object({
    rows: z.array(AdminNetworkOperator),
    total: z.number().int(),
    truncated: z.boolean(),
  }),
  pub: AdminNetworkSide,
  sub: AdminNetworkSide,
});
export type AdminNetworkResponse = z.infer<typeof AdminNetworkResponse>;

/* ════════════════════════ импорт документов (раздел 5.7) ════════════════════ */

/**
 * Строка списка заданий импорта.
 *
 * ⚠️ **Ни `title`, ни имени исходного файла здесь нет, и полей под них не
 * появится.** `doc_convert_job.title` это придуманное человеком название будущей
 * заготовки, то есть содержимое, а панель содержимого не читает (разделы 5.2 и 10
 * документа). Отлаживать по названию было бы удобнее, и это ровно тот довод,
 * которым такие границы и размываются. Задание опознаётся датой, владельцем,
 * префиксом `id`, числом страниц и типом исходника, а полный `id` из списка
 * находится в логах импорта, где название и так есть.
 */
export const AdminImportJobView = z.object({
  jobId: z.string().uuid(),
  createdAt: z.number().int(),
  /**
   * Когда задание двигалось последний раз.
   *
   * Нужен не ради полноты: у зависшего задания разница между этой датой и «сейчас»
   * это возраст молчания, и только по ней видно, что `running` уже никто не ведёт.
   */
  updatedAt: z.number().int(),
  ownerUserId: UserId,
  ownerDisplayName: DisplayName,
  state: ImportJobState,
  /**
   * Код отказа последней попытки.
   *
   * ⚠️ Непустой код бывает и у НЕ упавшего задания: `#retryOrFail` в
   * `apps/api/src/import/runner.ts` возвращает задание в `queued` (либо
   * `probing`), сохраняя код, и следующая попытка его перезапишет. То есть «код
   * есть» означает «последняя попытка не прошла», а не «задание провалилось», и
   * подпись на экране обязана говорить это, иначе разрез по причинам читается как
   * список провалов.
   */
  errorCode: z.string().nullable(),
  attempts: z.number().int(),
  donePages: z.number().int(),
  /**
   * Сколько страниц в исходнике. `null`, пока документ не разобран.
   *
   * ⚠️ **У успешного задания `donePages` меньше `totalPages` это норма, а не
   * оборванная работа.** Проверено на живых данных: бывает 4 из 12 и 120 из 300,
   * потому что страницы выбирает человек. Поэтому дробь «4 из 12» под словом
   * «готово» читалась бы как поломка, и раскладка живёт одной функцией
   * (`importProgressText`), а не собирается на экране заново.
   */
  totalPages: z.number().int().nullable(),
  /**
   * `media_file.content_type` исходника.
   *
   * ⚠️ `null` здесь это находка, а не норма: колонка в схеме `NOT NULL`, а
   * `ON DELETE CASCADE` уносит задание вместе с файлом, то есть задания без
   * исходника быть не должно. Показывается отдельным словом, а не прячется.
   */
  sourceContentType: z.string().nullable(),
  /**
   * Маркеры активного содержимого, найденные в файле (`/JavaScript`,
   * `/OpenAction`, `/Launch`, `/EmbeddedFile`, `/RichMedia`).
   *
   * Это предупреждение, а не отказ: активное содержимое у нас не исполняется
   * никогда, файл рисуется в картинки. Названия маркеров это структура PDF, а не
   * его содержание, поэтому запрет из шапки файла они не нарушают.
   */
  warnings: z.array(z.string()),
});
export type AdminImportJobView = z.infer<typeof AdminImportJobView>;

/**
 * Список заданий, постраничность keyset по `id`.
 *
 * Курсор это `id` последней показанной строки, как у списка людей: `id` это
 * `uuidv7`, то есть `ORDER BY id DESC` идёт по первичному ключу и совпадает с
 * хронологией. Сортировка по `created_at` потребовала бы второго ключа в курсоре
 * (две записи в одну миллисекунду), ничего не давая взамен.
 */
export const AdminImportJobListResponse = z.object({
  jobs: z.array(AdminImportJobView),
  /** `null` означает «страниц больше нет», то есть выдача полная. */
  nextCursor: z.string().uuid().nullable(),
});
export type AdminImportJobListResponse = z.infer<typeof AdminImportJobListResponse>;

/**
 * Счётчики заданий по исходам.
 *
 * ⚠️ **`cancelled` и `awaitingChoice` не отказы, и складывать их с `failed`
 * нельзя.** Отмену нажал человек, «ждёт выбора страниц» это вообще не исход, а
 * пауза в ожидании его же действия. Цена ошибки посчитана на dev-выборке из
 * семнадцати заданий: отказов там четыре, а «не готово» десять, то есть в
 * числителе набежало бы в два с половиной раза больше.
 *
 * ⚠️ Знаменатель это `finished`, то есть только `done` плюс `failed`, и на экране
 * он обязан быть подписан. Ошибка тут даёт противоположный перекос: на той же
 * выборке верное «4 из 11» превратилось бы в «4 из 17», то есть доля отказов
 * оказалась бы заниженной под собственной подписью.
 */
export const AdminImportTally = z.object({
  jobs: z.number().int(),
  done: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
  awaitingChoice: z.number().int(),
  /** `probing`, `queued`, `running`: про эти задания ещё ничего не известно. */
  pending: z.number().int(),
  /**
   * Знаменатель доли отказов: `done` плюс `failed`.
   *
   * Считает сервер, а не экран, потому что правило одно, а мест показа несколько
   * (плитка, таблица по людям, таблица по типам). Формула живёт в
   * `importFailureBase`.
   */
  finished: z.number().int(),
  /** Заданий с непустым `warnings`. */
  withWarnings: z.number().int(),
  /**
   * Заданий с записанным `error_code`, включая не упавшие.
   *
   * ⚠️ Отдельно от `failed` намеренно: это знаменатель разреза по причинам, и он
   * бывает больше числа отказов ровно на задания, вернувшиеся в очередь после
   * неудачной попытки (см. `errorCode`).
   */
  withErrorCode: z.number().int(),
});
export type AdminImportTally = z.infer<typeof AdminImportTally>;

/** Строка таблицы «по людям»: у кого сколько заданий и сколько из них упало. */
export const AdminImportOwnerRow = z.object({
  userId: UserId,
  displayName: DisplayName,
  jobs: z.number().int(),
  done: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
});
export type AdminImportOwnerRow = z.infer<typeof AdminImportOwnerRow>;

/**
 * Строка таблицы «по типу исходника».
 *
 * `contentType` это `null`, когда исходника не нашлось (см. `sourceContentType`).
 */
export const AdminImportTypeRow = z.object({
  contentType: z.string().nullable(),
  jobs: z.number().int(),
  done: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
});
export type AdminImportTypeRow = z.infer<typeof AdminImportTypeRow>;

/**
 * Разрез с потолком строк: три числа рядом с выдачей, как у всех остальных.
 *
 * `total` это сумма наблюдений по ВСЕМ группам, включая непоказанные, `distinct`
 * это число групп, `truncated` это признак обрезки. Молчаливой обрезки в панели не
 * бывает (правила 5.1 и 5.3), а `total` заодно сходится с `tally.jobs`, и это
 * бесплатная сверка глазами.
 */
function importGroup<T extends z.ZodTypeAny>(row: T) {
  return z.object({
    rows: z.array(row),
    total: z.number().int(),
    distinct: z.number().int(),
    truncated: z.boolean(),
  });
}

/**
 * Статистика импорта (раздел 5.7 документа).
 *
 * Импорт заявлен главной фичей продукта (раздел 6 CLAUDE.md, «PDF на входе,
 * черновик упражнений на выходе»), поэтому доля и причины отказов это первое, что
 * надо видеть: до этого экрана в панели был только счётчик заданий по состояниям в
 * сводке инвентаря, без разреза по людям и по причинам.
 *
 * Инструментирования не требует ни одной строки, как и инвентарь: всё уже лежит в
 * `doc_convert_job`.
 */
export const AdminImportStatsResponse = z.object({
  /** Кто в разрезе. `null` значит «по всем людям». */
  scope: z.object({ userId: UserId, displayName: DisplayName }).nullable(),
  tally: AdminImportTally,
  /** Состояния. Знаменатель это все задания разреза. */
  byState: AdminBreakdown,
  /**
   * ⚠️ **Главная таблица экрана**: сколько раз какая причина. Знаменатель это
   * задания с записанным кодом (`tally.withErrorCode`), а не все задания и не
   * только упавшие.
   */
  errors: AdminBreakdown,
  /**
   * Маркеры активного содержимого.
   *
   * ⚠️ Знаменатель это ВСЕ задания разреза, а не сумма по строкам, и это тот же
   * приём, что у причин ограничения качества на экране сети: вопрос «как часто
   * активное содержимое вообще встречается», а не «какой маркер чаще». Сумма по
   * строкам больше `tally.withWarnings`, потому что в одном файле маркеров бывает
   * несколько.
   */
  warnings: AdminBreakdown,
  /**
   * ⚠️ `null`, а не пустой массив, когда запрошен разрез по человеку: пустая
   * таблица читалась бы как «ни у кого нет заданий», а её просто не спрашивали.
   */
  owners: importGroup(AdminImportOwnerRow).nullable(),
  contentTypes: importGroup(AdminImportTypeRow),
});
export type AdminImportStatsResponse = z.infer<typeof AdminImportStatsResponse>;

/**
 * Знаменатель доли отказов: только завершённые задания.
 *
 * ⚠️ Функция ради одного сложения существует потому, что складывать сюда
 * `cancelled` и `awaiting_choice` соблазнительно и неверно, а мест, где нужен этот
 * знаменатель, четыре (плитка, две таблицы разрезов, строка сводки). Правило,
 * живущее в одном месте, не разъезжается по трём.
 */
export function importFailureBase(tally: { done: number; failed: number }): number {
  return tally.done + tally.failed;
}

/**
 * Прогресс задания словами.
 *
 * ⚠️ **У `done` знаменателя нет намеренно.** `done_pages` меньше `total_pages` у
 * успешного задания это норма: страницы выбирает человек, и «готово, 4 из 12»
 * читается как оборванная работа. Поэтому у готового задания число собранных
 * страниц стоит отдельно от числа страниц в файле, а дробью показываются только
 * те состояния, где знаменатель это действительно взятая работа.
 *
 * Возвращает текст, а не структуру, потому что различий тут ровно три и все они
 * словесные, а разбор по состояниям на экране означал бы то же правило, записанное
 * в разметке.
 */
export function importProgressText(job: {
  state: ImportJobState;
  donePages: number;
  totalPages: number | null;
}): string {
  const { donePages: done, totalPages: total } = job;
  if (job.state === "done") {
    return total === null ? `собрано ${done}` : `собрано ${done}, в файле ${total}`;
  }
  // До выбора страниц собирать ещё нечего, и нуль тут не измерение: пока не
  // разобрали документ, неизвестен даже знаменатель
  if (job.state === "probing" || job.state === "awaiting_choice") {
    return total === null ? "страницы ещё не сосчитаны" : `в файле ${total}`;
  }
  // queued, running, failed, cancelled: знаменатель это работа, которую задание
  // взяло, и дробь здесь как раз осмысленна
  return total === null ? `сделано ${done}` : `${done} из ${total}`;
}

/**
 * Пределы выдачи и окна, все в одном месте.
 *
 * ⚠️ Число, зашитое рядом с такой константой, это не дубль, а расхождение,
 * ждущее своего дня: ровно так `ADMIN_USER_EVENT_LIMIT` полгода совпадал с
 * `limit: 20` в запросе карточки случайно. Ни одна из этих величин не должна
 * встречаться в коде литералом.
 */

/** Сколько людей отдаём одной страницей списка. Больше на экран не влезает. */
export const ADMIN_USER_PAGE_SIZE = 50;
/**
 * Минимум символов в поиске по людям: по одной букве выдача это все записи,
 * только медленно.
 *
 * ⚠️ Число нужно обеим сторонам, поэтому и живёт здесь: сервер короткий поиск
 * игнорирует, а панель его не отправляет вовсе, чтобы в адресе не висел параметр,
 * который ничего не делает. Разойдясь, эти два места дали бы поиск, который в
 * адресе есть, а в выдаче не применён, то есть выдачу, читаемую как результат
 * поиска.
 */
export const ADMIN_SEARCH_MIN_CHARS = 2;
/** Дальше подстрока удлиняет не поиск, а запрос. */
export const ADMIN_SEARCH_MAX_CHARS = 100;
/** Сколько строк отдаём одной страницей ленты событий. */
export const ADMIN_EVENT_PAGE_SIZE = 100;
/**
 * Сколько живых сессий отдаём одной страницей.
 *
 * ⚠️ Раньше это был `ADMIN_SESSION_LIST_LIMIT = 200`, то есть потолок без курсора:
 * выдача обрезалась молча. Теперь это страница, и за ней есть `nextCursor`, а
 * значит величина выбирается по экрану, а не «с запасом на всех»: сто строк это
 * уже больше, чем читают глазами за один раз.
 */
export const ADMIN_SESSION_PAGE_SIZE = 100;
/** Сколько последних событий входа кладём в карточку человека. */
export const ADMIN_USER_EVENT_LIMIT = 20;
/**
 * Сколько сессий кладём в карточку человека.
 *
 * Здесь потолок, а не постраничность, и это осознанно: карточка отвечает на
 * вопрос «что с ним сейчас», а полная история входов живёт на экране событий.
 * Цена потолка это `sessionsTruncated` в ответе, см. `AdminUserDetail`.
 */
export const ADMIN_CARD_SESSION_LIMIT = 50;
/**
 * Сколько строк отдаём в разрезах инвентаря и сети.
 *
 * Один потолок на все такие таблицы намеренно: у них одна форма ответа
 * (`rows` плюс `distinct` плюс `truncated`) и одна цена ошибки, а два числа
 * означали бы два вопроса «а почему тут двадцать, а там тридцать».
 *
 * ⚠️ Потолок здесь не про производительность, а про экран: группировка всё равно
 * идёт по всей выборке, отсекается только хвост показа. Поэтому `distinct` в
 * ответе это точное число различных значений, а не число показанных.
 */
export const ADMIN_BUCKET_LIMIT = 20;
/** Сколько людей показываем в разрезе инвентаря по людям. */
export const ADMIN_INVENTORY_OWNER_LIMIT = 50;
/**
 * Сколько заданий импорта отдаём одной страницей списка.
 *
 * Пятьдесят, а не сотня как у лент событий: у строки задания девять столбцов, и
 * читают этот список глазами сверху вниз, а не ищут в нём одну строку. Дальше есть
 * `nextCursor`, поэтому величина выбирается по экрану, а не «с запасом на всех».
 */
export const ADMIN_IMPORT_PAGE_SIZE = 50;
/**
 * Потолок длины фильтра по коду отказа.
 *
 * ⚠️ Не переиспользуем `ADMIN_SEARCH_MAX_CHARS`: там подстрока для `ILIKE`, а
 * здесь точное совпадение с идентификатором вида `pdf_encrypted`. Общее число
 * связало бы поиск по людям с фильтром импорта, и первое же изменение одного
 * молча поехало бы во второе.
 */
export const ADMIN_ERROR_CODE_MAX_CHARS = 64;
/**
 * Окно наблюдения на экране сети по умолчанию, в сутках.
 *
 * Тридцать, а не всё хранение: телеметрия отвечает на вопрос «как сейчас», а
 * полугодовая выборка размывает эффект от смены конфигурации SFU до
 * незаметности. Окно меняется параметром `days` в адресе.
 */
export const ADMIN_NETWORK_WINDOW_DAYS = 30;
/**
 * Предел окна.
 *
 * ⚠️ Совпадает со сроком хранения телеметрии из 5.10 CLAUDE.md (180 суток) не
 * случайно: за этой границей данных нет физически, и окно шире означало бы
 * подпись «за год», под которой лежит полгода. Заказ окна больше предела это
 * отказ `invalid_window`, а не молчаливое усечение до предела.
 */
export const ADMIN_NETWORK_WINDOW_MAX_DAYS = 180;
